import { type FC } from 'react';
import styles from '../styles/final-confirmation.module.scss';
import PageTitle from '../components/page-title';
import PageActions from '../components/page-actions';
import AttentionNotice from '../components/attention-notice';
import FormField from '../components/form-field';
import type {
    CustomerLineStatus,
    ExistingRepairBooking,
    NewRepairBooking,
    PersonalInfo,
    PersonalInfoConfig,
} from '../models/repair-bookings';
import useSubmit from '../hooks/use-submit';
import { getTimeSlotString } from '../utils/helpers';

type FinalConfirmationProps = {
    booking: NewRepairBooking | ExistingRepairBooking;
    personalInfoConfig: PersonalInfoConfig;
    onSubmit: () => Promise<void>;
    onCancel: () => void;
};

const FinalConfirmation: FC<FinalConfirmationProps> = (props) => {
    const selectedSlot = props.booking.selectedSlot;

    const { isSubmitting, submit } = useSubmit(props.onSubmit);

    return (
        <div className={styles.finalConfirmation}>
            {selectedSlot && (
                <>
                    {props.booking.type === 'new' ? (
                        <>
                            <PageTitle title="予約内容をご確認ください" />
                            <div className={styles.confirmationNote}>
                                まだ予約は確定していません
                            </div>
                            <div className={styles.confirmationContent}>
                                <ReadOnlyField
                                    label="来店目的"
                                    value={[
                                        props.booking.repairOptions.map((s) => s.name).join(', '),
                                        props.booking.product!.name,
                                    ].join('｜')}
                                />
                                <ReadOnlyField
                                    label="店舗"
                                    value={props.booking.shop?.name ?? ''}
                                />
                                <ReadOnlyField
                                    label="予約日時"
                                    value={`${getTimeSlotString(selectedSlot)}`}
                                />
                                <PersonalInfoFields
                                    personalInfo={props.booking.personalInfo}
                                    personalInfoConfig={props.personalInfoConfig}
                                />
                            </div>
                            <AttentionNotice
                                className={styles.attentionNotice}
                                note="※ ご登録いただいた電話番号、もしくはメールアドレスにご連絡を行うことがございますのでご了承ください。"
                            />
                        </>
                    ) : (
                        props.booking.type === 'existing' && (
                            <>
                                <PageTitle title="変更した予約内容をご確認ください" />
                                <div className={styles.confirmationNote}>
                                    まだ変更内容は確定していません
                                </div>
                                <div className={styles.confirmationContent}>
                                    <ReadOnlyField
                                        label="予約番号"
                                        value={props.booking.confirmationId}
                                    />
                                    <ReadOnlyField
                                        label="予約店舗"
                                        value={props.booking.shop?.name ?? ''}
                                    />
                                    <ReadOnlyField
                                        label="予約日時"
                                        value={`${getTimeSlotString(selectedSlot)}`}
                                    />
                                    {props.booking.personalInfo &&
                                        props.personalInfoConfig.granularity === 'extended' && (
                                            <PersonalInfoFields
                                                personalInfo={props.booking.personalInfo}
                                                personalInfoConfig={props.personalInfoConfig}
                                            />
                                        )}
                                </div>
                            </>
                        )
                    )}
                    <PageActions
                        actionTitle="上記内容で確定する"
                        actionButtonDisabled={isSubmitting}
                        actionButtonLoading={isSubmitting}
                        onOk={submit}
                        onCancel={props.onCancel}
                    />
                </>
            )}
        </div>
    );
};

type PersonalInfoFieldsProps = {
    personalInfo: PersonalInfo;
    personalInfoConfig: PersonalInfoConfig;
};

const PersonalInfoFields: FC<PersonalInfoFieldsProps> = ({ personalInfo, personalInfoConfig }) => {
    const lineStatusDisplayName = (lineStatuses: CustomerLineStatus[]) => {
        const lineStatusKey = personalInfo.line_status;
        if (!lineStatusKey) {
            return '';
        }
        return lineStatuses.find((status) => status.key === lineStatusKey)?.name ?? lineStatusKey;
    };

    return (
        <>
            <div className={styles.separator} />
            <div className={styles.formRow}>
                <ReadOnlyField label="姓" value={personalInfo.last_name} />
                <ReadOnlyField label="名" value={personalInfo.first_name} />
            </div>
            <div className={styles.formRow}>
                <ReadOnlyField label="姓（カナ）" value={personalInfo.last_name_pronunciation} />
                <ReadOnlyField label="名（カナ）" value={personalInfo.first_name_pronunciation} />
            </div>
            {personalInfoConfig.granularity === 'extended' && (
                <ReadOnlyField label="生年月日" value={personalInfo.birthday ?? ''} />
            )}
            <ReadOnlyField tel label="携帯電話番号" value={personalInfo.phone_number} />
            {personalInfoConfig.granularity === 'extended' && (
                <>
                    <ReadOnlyField
                        label="au/UQ回線状況"
                        value={lineStatusDisplayName(personalInfoConfig.lineStatuses)}
                    />
                    <ReadOnlyField
                        label="加入者コード"
                        value={personalInfo.subscriber_code ?? ''}
                    />
                </>
            )}
            <ReadOnlyField tel label="連絡先電話番号" value={personalInfo.alt_phone_number} />
            <ReadOnlyField email label="電子メール" value={personalInfo.email} />
            <ReadOnlyField textarea label="店舗へのご連絡事項" value={personalInfo.notes} />
            {personalInfoConfig.granularity === 'extended' && (
                <ReadOnlyField
                    label="故障紛失サポート"
                    value={personalInfo.is_warranty_member ? '会員' : '非会員'}
                />
            )}
        </>
    );
};

const ReadOnlyField: FC<{
    label: string;
    value: string;
    tel?: boolean;
    email?: boolean;
    date?: boolean;
    textarea?: boolean;
}> = ({ label, value, tel, email, date, textarea }) => {
    return (
        <FormField
            readOnly={true}
            inputType={
                tel ? 'tel' : email ? 'email' : date ? 'date' : textarea ? 'textarea' : 'text'
            }
            label={label}
            value={value}
        />
    );
};

export default FinalConfirmation;
